import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Table, Badge, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import axiosInstance from '../../services/axiosConfig';
import 'bootstrap-icons/font/bootstrap-icons.css';

const TeamManagement = () => {
  const { id } = useParams();
  const eventId = id; // Route uses :id parameter
  const navigate = useNavigate();

  // State
  const [event, setEvent] = useState(null);
  const [teams, setTeams] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Scaletta state
  const [performanceOrder, setPerformanceOrder] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');

  // Details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch event details
  useEffect(() => {
    fetchEventDetails();
    fetchTeams();
    fetchAssociations();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get(`/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvent(response.data.event || response.data);
    } catch (err) {
      console.error('Errore caricamento evento:', err);
      setError('Impossibile caricare i dettagli dell\'evento');
    }
  };

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axiosInstance.get(`/api/events/${eventId}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.success) {
        const teamsData = response.data.teams || [];
        console.log('Teams caricati:', teamsData.length);
        
        // Ordina per numero d'ordine se presente, altrimenti per data registrazione
        const sortedTeams = teamsData.sort((a, b) => {
          if (a.performanceOrder !== undefined && b.performanceOrder !== undefined) {
            return a.performanceOrder - b.performanceOrder;
          }
          return new Date(a.registration_date) - new Date(b.registration_date);
        });
        
        setTeams(sortedTeams);
        
        // Inizializza l'ordine di performance
        const order = sortedTeams
          .filter(t => t.performanceOrder !== undefined)
          .sort((a, b) => a.performanceOrder - b.performanceOrder);
        setPerformanceOrder(order);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Errore caricamento squadre:', err);
      setError('Impossibile caricare le squadre');
      setLoading(false);
    }
  };

  const fetchAssociations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get('/association-data', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.associations) {
        setAssociations(response.data.associations);
      }
    } catch (err) {
      console.error('Errore caricamento associazioni:', err);
    }
  };

  const getAssociationName = (associationId) => {
    const association = associations.find(a => a.id === parseInt(associationId));
    return association ? association.name : `Associazione ${associationId}`;
  };

  const isAttachedFile = (value) => {
    return (
      value &&
      typeof value === 'object' &&
      (value.filename || value.path || value.savedFilename || value.url)
    );
  };

  const getTeamFileDownloadUrl = (team, fieldName, fileData) => {
    if (!fileData) return '';

    if (typeof fileData.url === 'string' && fileData.url.length > 0) {
      return fileData.url;
    }

    if (typeof fileData.path === 'string' && fileData.path.length > 0) {
      return fileData.path;
    }

    if (typeof fileData.savedFilename === 'string' && fileData.savedFilename.length > 0) {
      return `/uploads/${fileData.savedFilename}`;
    }

    return `/api/events/${eventId}/teams/${team._id}/files/${encodeURIComponent(fieldName)}`;
  };

  const handleDownloadTeamFile = (team, fieldName, fileData) => {
    const downloadUrl = getTeamFileDownloadUrl(team, fieldName, fileData);
    if (!downloadUrl) {
      setError('File non disponibile per il download');
      return;
    }

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = fileData?.filename || '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSetOrder = (team) => {
    setSelectedTeam(team);
    setOrderNumber(team?.performanceOrder != null ? team.performanceOrder.toString() : '');
    setShowOrderModal(true);
  };

  const savePerformanceOrder = async () => {
    try {
      const token = localStorage.getItem('token');
      const order = parseInt(orderNumber);
      
      if (isNaN(order) || order < 1) {
        setError('Inserisci un numero valido maggiore di 0');
        return;
      }

      // Aggiorna l'ordine della squadra
      await axiosInstance.put(
        `/api/events/${eventId}/teams/${selectedTeam._id}`,
        { performanceOrder: order },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Ordine di esibizione aggiornato con successo');
      setShowOrderModal(false);
      fetchTeams();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Errore aggiornamento ordine:', err);
      setError('Impossibile aggiornare l\'ordine');
    }
  };

  const handleShowDetails = (team) => {
    setSelectedTeam(team);
    setShowDetailsModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const buildScalettaCategoryLabel = (team) => {
    const ageBand = team.formData?.ageBand?.trim();
    const style = team.formData?.style?.trim();
    const category = team.formData?.category?.trim();

    const normalizedStyle = style
      ? style.replace(/^danza\s+/i, '').replace(/\s+/g, ' ')
      : '';

    const label = [ageBand, normalizedStyle, category]
      .filter(Boolean)
      .join(' ')
      .trim();

    return label ? label.toUpperCase() : 'ESIBIZIONE';
  };

  const formatScalettaDateTitle = () => {
    const dateValue = event?.eventDate || event?.date || event?.startDate;
    if (!dateValue) {
      return new Date().toLocaleDateString('it-IT', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      }).toUpperCase();
    }

    return new Date(dateValue).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).toUpperCase();
  };

  const exportScaletta = () => {
    const orderedTeams = teams
      .filter(t => t.performanceOrder != null)
      .sort((a, b) => a.performanceOrder - b.performanceOrder);

    if (orderedTeams.length === 0) {
      setError('Nessuna squadra in scaletta da esportare');
      return;
    }

    const titleDate = formatScalettaDateTitle();

    let previousCategory = '';
    let rowsHtml = '';

    orderedTeams.forEach((team) => {
      const categoryLabel = buildScalettaCategoryLabel(team);
      const associationName = getAssociationName(team.association_id);
      const choreographyName =
        team.formData?.choreographyTitle ||
        team.formData?.titolobrano ||
        team.name ||
        'DA COMUNICARE';
      const duration = team.formData?.choreographyEffectiveDuration || '';

      if (previousCategory && previousCategory !== categoryLabel) {
        rowsHtml += '<tr class="section-gap"><td colspan="4"></td></tr>';
      }

      rowsHtml += `
        <tr>
          <td class="col-category">${escapeHtml(categoryLabel)}</td>
          <td class="col-association">${escapeHtml(associationName.toUpperCase())}</td>
          <td class="col-choreography">${escapeHtml(String(choreographyName).toUpperCase())}</td>
          <td class="col-duration">${escapeHtml(duration)}</td>
        </tr>
      `;

      previousCategory = categoryLabel;
    });

    const htmlContent = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            body { font-family: "Times New Roman", serif; }
            table { border-collapse: collapse; width: 100%; }
            td, th { border: 1px solid #666; padding: 4px 8px; }
            .title-row th {
              font-size: 28px;
              font-weight: 700;
              text-align: center;
              border: 1px solid #666;
              background: #f5f5f5;
              padding: 10px;
              letter-spacing: 1px;
            }
            .header-row th {
              background: #b7b7b7;
              font-size: 18px;
              font-weight: 600;
              text-align: center;
            }
            .col-category { width: 34%; font-style: italic; font-weight: 600; }
            .col-association { width: 30%; text-align: center; font-style: italic; }
            .col-choreography { width: 28%; text-align: center; font-style: italic; }
            .col-duration { width: 8%; text-align: right; }
            .section-gap td {
              background: #d9d9d9;
              height: 10px;
              border-top: 1px solid #666;
              border-bottom: 1px solid #666;
            }
          </style>
        </head>
        <body>
          <table>
            <tr class="title-row">
              <th colspan="4">SCALETTA - ${escapeHtml(titleDate)}</th>
            </tr>
            <tr class="header-row">
              <th>categoria</th>
              <th>nome associazione</th>
              <th>nome coreografia</th>
              <th>tempo</th>
            </tr>
            ${rowsHtml}
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([`\ufeff${htmlContent}`], {
      type: 'application/vnd.ms-excel;charset=utf-8;'
    });
    const downloadUrl = URL.createObjectURL(blob);
    const safeTitle = String(event?.title || 'evento').replace(/[\\/:*?"<>|]/g, '_');
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `scaletta_${safeTitle}_${new Date().toISOString().split('T')[0]}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);

    setSuccess('Scaletta esportata con successo');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Caricamento squadre...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 style={{ color: '#006e39' }}>
                <i className="bi bi-people-fill me-2"></i>
                Gestione Squadre
              </h2>
              {event && (
                <p className="text-muted mb-0">
                  <i className="bi bi-calendar-event me-2"></i>
                  {event.title}
                </p>
              )}
            </div>
            <div>
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={() => navigate(`/admin/events/${eventId}/participants`)}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Torna ai Partecipanti
              </Button>
              <Button
                variant="success"
                onClick={exportScaletta}
                disabled={teams.filter(t => t.performanceOrder !== undefined).length === 0}
                style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
              >
                <i className="bi bi-download me-2"></i>
                Esporta Scaletta
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          <i className="bi bi-check-circle-fill me-2"></i>
          {success}
        </Alert>
      )}

      {/* Statistics */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Squadre Totali</h6>
                  <h3 className="mb-0" style={{ color: '#006e39' }}>{teams.length}</h3>
                </div>
                <i className="bi bi-people-fill" style={{ fontSize: '2rem', color: '#006e39', opacity: 0.3 }}></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Partecipanti Totali</h6>
                  <h3 className="mb-0" style={{ color: '#006e39' }}>
                    {teams.reduce((sum, team) => sum + team.members.length, 0)}
                  </h3>
                </div>
                <i className="bi bi-person-fill" style={{ fontSize: '2rem', color: '#006e39', opacity: 0.3 }}></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">In Scaletta</h6>
                  <h3 className="mb-0" style={{ color: '#006e39' }}>
                    {teams.filter(t => t.performanceOrder !== undefined).length}
                  </h3>
                </div>
                <i className="bi bi-list-ol" style={{ fontSize: '2rem', color: '#006e39', opacity: 0.3 }}></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Associazioni</h6>
                  <h3 className="mb-0" style={{ color: '#006e39' }}>
                    {new Set(teams.map(t => t.association_id)).size}
                  </h3>
                </div>
                <i className="bi bi-building" style={{ fontSize: '2rem', color: '#006e39', opacity: 0.3 }}></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Teams Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
          <h5 className="mb-0">
            <i className="bi bi-list-task me-2"></i>
            Elenco Squadre
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {teams.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
              <p className="text-muted mt-3">Nessuna squadra registrata per questo evento</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ width: '80px' }}>Ordine</th>
                  <th>Nome Squadra</th>
                  <th>Associazione</th>
                  <th>Membri</th>
                  <th>Categoria</th>
                  <th>Data Iscrizione</th>
                  <th style={{ width: '200px' }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team, index) => (
                  <tr key={team._id}>
                    <td>
                      {team.performanceOrder !== undefined ? (
                        <Badge bg="success" style={{ fontSize: '1rem', padding: '8px 12px' }}>
                          #{team.performanceOrder}
                        </Badge>
                      ) : (
                        <Badge bg="secondary">-</Badge>
                      )}
                    </td>
                    <td>
                      <strong>{team.name}</strong>
                      {team.formData?.choreographyTitle && (
                        <><br />
                        <small className="text-muted">
                          <i className="bi bi-music-note me-1"></i>
                          {team.formData.choreographyTitle}
                        </small></>
                      )}
                    </td>
                    <td>
                      <small>{getAssociationName(team.association_id)}</small>
                    </td>
                    <td>
                      <Badge bg="info">
                        <i className="bi bi-people-fill me-1"></i>
                        {team.members.length}
                      </Badge>
                    </td>
                    <td>
                      {team.formData?.category ? (
                        <small className="text-muted">{team.formData.category}</small>
                      ) : (
                        <small className="text-muted">N/A</small>
                      )}
                    </td>
                    <td>
                      <small>{formatDate(team.registration_date)}</small>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => handleShowDetails(team)}
                        title="Visualizza dettagli"
                      >
                        <i className="bi bi-eye"></i>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => handleSetOrder(team)}
                        title="Imposta ordine"
                        style={{ borderColor: '#006e39', color: '#006e39' }}
                      >
                        <i className="bi bi-sort-numeric-down"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Ordine Esibizione */}
      <Modal show={showOrderModal} onHide={() => setShowOrderModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: '#006e39', color: 'white' }}>
          <Modal.Title>
            <i className="bi bi-sort-numeric-down me-2"></i>
            Imposta Ordine Esibizione
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTeam && (
            <>
              <p>
                <strong>Squadra:</strong> {selectedTeam.name}
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Numero d'Ordine</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="Es. 1, 2, 3..."
                />
                <Form.Text className="text-muted">
                  Inserisci la posizione in cui la squadra si esibirà
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
            Annulla
          </Button>
          <Button
            variant="success"
            onClick={savePerformanceOrder}
            style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
          >
            <i className="bi bi-check-lg me-2"></i>
            Salva
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Dettagli Squadra */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: '#006e39', color: 'white' }}>
          <Modal.Title>
            <i className="bi bi-info-circle me-2"></i>
            Dettagli Squadra
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTeam && (
            <>
              <h5 className="mb-3">{selectedTeam.name}</h5>
              
              {/* Informazioni Base */}
              <Card className="mb-3 border-0" style={{ backgroundColor: '#f8f9fa' }}>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Associazione:</strong><br />
                        <small>{getAssociationName(selectedTeam.association_id)}</small>
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Data Iscrizione:</strong><br />
                        <small>{formatDate(selectedTeam.registration_date)}</small>
                      </p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Form Data */}
              {selectedTeam.formData && Object.keys(selectedTeam.formData).length > 0 && (
                <Card className="mb-3">
                  <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
                    <strong>Informazioni Iscrizione</strong>
                  </Card.Header>
                  <Card.Body>
                    {Object.entries(selectedTeam.formData).map(([key, value]) => {
                      if (isAttachedFile(value)) {
                        const isAudio = value?.mimetype?.startsWith('audio/') || key.toLowerCase().includes('audio');
                        const fileUrl = getTeamFileDownloadUrl(selectedTeam, key, value);

                        return (
                          <div key={key} className="mb-3">
                            <strong>{key.replace(/([A-Z])/g, ' $1').trim()}:</strong>
                            <div className="mt-1 d-flex align-items-center gap-2 flex-wrap">
                              <Badge bg={isAudio ? 'success' : 'primary'}>
                                <i className="bi bi-file-earmark me-1"></i>
                                {value.filename || 'File allegato'}
                              </Badge>
                              <Button
                                size="sm"
                                variant={isAudio ? 'outline-success' : 'outline-primary'}
                                onClick={() => handleDownloadTeamFile(selectedTeam, key, value)}
                              >
                                <i className="bi bi-download me-1"></i>
                                Scarica
                              </Button>
                            </div>
                            {isAudio && fileUrl && (
                              <div className="mt-2">
                                <audio controls preload="none" src={fileUrl} style={{ width: '100%', maxWidth: '420px' }}>
                                  Il tuo browser non supporta la riproduzione audio.
                                </audio>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      return (
                        <div key={key} className="mb-2">
                          <strong>{key.replace(/([A-Z])/g, ' $1').trim()}:</strong> {value}
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              )}

              {/* Membri */}
              <Card>
                <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
                  <strong>
                    <i className="bi bi-people-fill me-2"></i>
                    Membri ({selectedTeam.members.length})
                  </strong>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th>Nome</th>
                        <th>Cognome</th>
                        <th>Codice Fiscale</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTeam.members.map((member, idx) => (
                        <tr key={idx}>
                          <td>{member.first_name || 'N/A'}</td>
                          <td>{member.last_name || 'N/A'}</td>
                          <td><small>{member.fiscal_code || 'N/A'}</small></td>
                          <td><small>{member.email || 'N/A'}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TeamManagement;
